import { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import CloseIcon from '@ancon/wildcat-ui/shared/icons/close.svg'
import { PurchaseProductDetails } from '@ancon/wildcat-types'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'

import Button from '../../../app/components/Button'
import HeadingText from '../../../app/components/HeadingText'
import { ConfigureProductUpsellGroupFormState } from '../../types'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import {
  productDetailsSelector,
  productsDetailsUpsellGroupNameSelector,
  productsDetailsUpsellGroupProductsSelector,
} from '../../store/productSelectors'
import { AppTestIds } from '../../../app/constants'

import styles from './ConfigureUpsellGroupForm.module.scss'
import UpsellProductCard from './UpsellProductCard'

function getInitialUpsellGroupState(productDetails: PurchaseProductDetails) {
  const { upsellGroups } = productDetails

  if (!!upsellGroups.length && !!upsellGroups[0].products.length) {
    const { products, id } = upsellGroups[0]

    return {
      selectedUpsellGroups: products.map(product => ({
        variantId: product.variantId,
        selectedCount: 0,
      })),
      upsellGroupId: id,
    }
  }

  return {
    selectedUpsellGroups: [],
    upsellGroupId: undefined,
  }
}

type ConfigureUpsellGroupFormProps = {
  onDismiss: () => void
  onSubmit: (data: ConfigureProductUpsellGroupFormState) => void
}

export default function ConfigureUpsellGroupForm({
  onDismiss,
  onSubmit,
}: ConfigureUpsellGroupFormProps) {
  const { t } = useTranslation('common')
  const productDetails = useAppSelector(productDetailsSelector)
  const upsellGroupProducts = useAppSelector(
    productsDetailsUpsellGroupProductsSelector,
  )
  const upsellGroupName = useAppSelector(productsDetailsUpsellGroupNameSelector)
  const contentContainerRef = useRef<HTMLDivElement>(null)
  const [showHeader, setShowHeader] = useState(false)

  const form = useForm<ConfigureProductUpsellGroupFormState>({
    defaultValues: {
      ...getInitialUpsellGroupState(productDetails!),
    },
  })
  const { handleSubmit } = form

  useEffect(() => {
    const contentContainer = contentContainerRef.current

    function handleScroll() {
      if (contentContainer) {
        const scrollTop = contentContainer.scrollTop ?? 0
        setShowHeader(scrollTop > 80)
      }
    }

    if (contentContainer) {
      contentContainer.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (contentContainer) {
        contentContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  const title =
    upsellGroupName || t('components.productDetailsModal.commonAdditions')

  return (
    <FormProvider
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...form}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div
          className={styles.container}
          ref={contentContainerRef}
          data-testid={AppTestIds.Order.Modal.ProductUpsellModal.Container}
        >
          <Button
            variant="secondary"
            onClick={onDismiss}
            data-testid={AppTestIds.Order.Modal.ProductUpsellModal.CloseButton}
          >
            <CloseIcon />
          </Button>

          <div className={styles.topContainer}>
            <div
              className={clsx(styles.floatingHeader, {
                [styles.show]: showHeader,
              })}
            >
              <HeadingText as="h2">{title}</HeadingText>
            </div>

            <HeadingText as="h2">{title}</HeadingText>

            <div className={styles.upsellListing}>
              {upsellGroupProducts.map((upsellProduct, i) => (
                <UpsellProductCard
                  key={upsellProduct.variantId}
                  upsellProduct={upsellProduct}
                  index={i}
                />
              ))}
            </div>
          </div>

          <div className={styles.footerContainer}>
            <Button
              type="submit"
              data-testid={
                AppTestIds.Order.Modal.ProductUpsellModal.ContinueButton
              }
            >
              {t('components.productDetailsModal.continue')}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
